import { toMoney } from "shared/utils";
import {
  IncomeReverifyRequestCanceledActivity,
  IncomeReverifyRequestCompletedActivity,
  IncomeReverifyRequestCreatedActivity,
  IncomeReverifyRequestFailedActivity,
  ReverifyCanceledActivity,
  ReverifyCompletedActivity,
  ReverifyRequestedActivity
} from "../types";
import { getFullName } from "shared/utils/get-full-name";
import { verificationSourceType } from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils";
import { VerifyMethodType } from "shared/types";

export const ReverifyCanceled = ({
  activityItem
}: {
  activityItem: ReverifyCanceledActivity;
}) => {
  return (
    <div>
      <h2 className="text-base text-content-1000 mb-2">
        <span className="font-bold">Income source re-verification</span> was
        canceled by {activityItem.author.name}
      </h2>
      <p className="text-sm text-content-400">
        for{" "}
        {activityItem.details.contact.first_name
          ? getFullName({
              firstName: activityItem.details.contact.first_name,
              lastName: activityItem.details.contact.last_name,
              middleName: activityItem.details.contact.middle_name,
              short: true
            })
          : activityItem.details.contact.email || "applicant"}{" "}
        — Income source: {activityItem.details.income.name}
      </p>
    </div>
  );
};

export const ReverifyRequested = ({
  activityItem
}: {
  activityItem: ReverifyRequestedActivity;
}) => {
  return (
    <div>
      <h2 className="text-base text-content-1000 mb-2">
        <span className="font-bold">Income source re-verification</span> was
        initiated by {activityItem.author.name}
      </h2>
      <p className="text-sm text-content-400">
        for{" "}
        {activityItem.details.contact.first_name
          ? getFullName({
              firstName: activityItem.details.contact.first_name,
              lastName: activityItem.details.contact.last_name,
              middleName: activityItem.details.contact.middle_name,
              short: true
            })
          : activityItem.details.contact.email || "applicant"}{" "}
        — Income source: {activityItem.details.income.name}
      </p>
    </div>
  );
};

export const ReverifyCompleted = ({
  activityItem
}: {
  activityItem: ReverifyCompletedActivity;
}) => {
  return (
    <div className="w-full">
      <h2 className="text-base text-content-1000 mb-2">
        <span className="font-bold">Income source re-verification</span> was
        completed
      </h2>
      <p className="text-sm text-content-400 border-b border-alpha-40 mb-2 pb-2">
        for{" "}
        {activityItem.details.contact.first_name
          ? getFullName({
              firstName: activityItem.details.contact.first_name,
              lastName: activityItem.details.contact.last_name,
              middleName: activityItem.details.contact.middle_name,
              short: true
            })
          : activityItem.details.contact.email || "applicant"}{" "}
        — Income source: {activityItem.details.income.name}
      </p>

      <ul className="space-y-2 list-disc pl-4 text-content-400 text-sm">
        <li>
          Gross monthly income: updated from{" "}
          <span className="font-medium">
            {toMoney(activityItem.details.previous_gross_monthly || 0)}
          </span>{" "}
          to{" "}
          <span className="font-medium">
            {toMoney(activityItem.details.current_gross_monthly || 0)}
          </span>
        </li>

        <li>
          Net monthly income: updated from{" "}
          <span className="font-medium">
            {toMoney(activityItem.details.previous_net_monthly || 0)}
          </span>{" "}
          to{" "}
          <span className="font-medium">
            {toMoney(activityItem.details.current_net_monthly || 0)}
          </span>
        </li>
      </ul>
    </div>
  );
};

const ReverifyRequestContactLabel = ({
  activityItem
}: {
  activityItem:
    | IncomeReverifyRequestCreatedActivity
    | IncomeReverifyRequestCanceledActivity
    | IncomeReverifyRequestCompletedActivity
    | IncomeReverifyRequestFailedActivity;
}) => {
  const { contact, reverify_request, income } = activityItem.details;
  const contactLabel = contact?.first_name
    ? getFullName({
        firstName: contact?.first_name,
        lastName: contact?.last_name,
        middleName: contact?.middle_name,
        short: true
      })
    : contact?.email || "applicant";

  return (
    <>
      for {contactLabel}
      {reverify_request.income_type ? ` — Income source: ${income?.name}` : ""}
    </>
  );
};

const ReverifyRequestMethodsLabel = ({
  activityItem
}: {
  activityItem:
    | IncomeReverifyRequestCreatedActivity
    | IncomeReverifyRequestCanceledActivity
    | IncomeReverifyRequestCompletedActivity
    | IncomeReverifyRequestFailedActivity;
}) => {
  const { methods } = activityItem.details.reverify_request;
  const { nonDocumentMethods, documentMethods } = methods.reduce<{
    nonDocumentMethods: string[];
    documentMethods: string[];
  }>(
    (acc, method) => {
      if ([VerifyMethodType.Bank, VerifyMethodType.Payroll].includes(method))
        acc.nonDocumentMethods.push(verificationSourceType[method]);

      if (
        [
          VerifyMethodType.Paystub,
          VerifyMethodType.W2,
          VerifyMethodType.Form1099,
          VerifyMethodType.BankStatement,
          VerifyMethodType.BenefitsStatement
        ].includes(method)
      )
        acc.documentMethods.push(verificationSourceType[method]);

      return acc;
    },
    {
      nonDocumentMethods: [],
      documentMethods: []
    }
  );

  if (!nonDocumentMethods.length && !documentMethods.length) return null;

  const parts: string[] = [];
  if (nonDocumentMethods.length) parts.push(nonDocumentMethods.join(", "));
  if (documentMethods.length)
    parts.push(`Documents: ${documentMethods.join(", ")}`);

  return <>Verification methods — {parts.join(", ")}</>;
};

export const IncomeReverifyRequestCreated = ({
  activityItem
}: {
  activityItem: IncomeReverifyRequestCreatedActivity;
}) => {
  return (
    <div>
      <h2 className="text-base text-content-1000 mb-2">
        <span className="font-bold">Income source re-verification request</span>{" "}
        was sent by {activityItem.author.name}
      </h2>
      <p className="text-sm text-content-400">
        <ReverifyRequestContactLabel activityItem={activityItem} />
      </p>
      <p className="text-sm text-content-400">
        <ReverifyRequestMethodsLabel activityItem={activityItem} />
      </p>
    </div>
  );
};

export const IncomeReverifyRequestCanceled = ({
  activityItem
}: {
  activityItem: IncomeReverifyRequestCanceledActivity;
}) => {
  return (
    <div>
      <h2 className="text-base text-content-1000 mb-2">
        <span className="font-bold">
          Income re-verification request was canceled
        </span>{" "}
        by {activityItem.author.name}
      </h2>
      <p className="text-sm text-content-400">
        <ReverifyRequestContactLabel activityItem={activityItem} />
      </p>
      <p className="text-sm text-content-400">
        <ReverifyRequestMethodsLabel activityItem={activityItem} />
      </p>
    </div>
  );
};

export const IncomeReverifyRequestCompleted = ({
  activityItem
}: {
  activityItem: IncomeReverifyRequestCompletedActivity;
}) => {
  return (
    <div>
      <h2 className="text-base text-content-1000 mb-2 font-bold">
        <span className="font-bold">Income source re-verification request</span>{" "}
        was completed
      </h2>
      <p className="text-sm text-content-400">
        <ReverifyRequestContactLabel activityItem={activityItem} />
      </p>
      <p className="text-sm text-content-400">
        <ReverifyRequestMethodsLabel activityItem={activityItem} />
      </p>
    </div>
  );
};

export const IncomeReverifyRequestFailed = ({
  activityItem
}: {
  activityItem: IncomeReverifyRequestFailedActivity;
}) => {
  return (
    <div>
      <h2 className="text-base text-content-1000 mb-2">
        <span className="font-bold">Income source re-verification</span> failed
      </h2>
      <p className="text-sm text-content-400">
        <ReverifyRequestContactLabel activityItem={activityItem} />
      </p>
      <p className="text-sm text-content-400">
        <ReverifyRequestMethodsLabel activityItem={activityItem} />
      </p>
    </div>
  );
};
